import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import axios from 'axios';
import InsufficientCreditsAlert from './InsufficientCreditsAlert';
import LoadingCard from './LoadingCard';
import { splitLanguagePair } from '../utils/languagePairs';
import type { CreditTopUpContext } from '../types/creditTopUp';
import './TermScanLaunchModal.css';
import { glossaryScanCost, launchRequestIdentity, LaunchRequestIdentity, GlossaryScanEstimate } from '../utils/glossaryScan';

const { Text } = Typography;

const formatCredits = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Loading...';
  return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)));
};

interface TermScanLaunchModalProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  pair: string;
  apiBase: string;
  resolvedUserId?: string;
  auth?: any;
  availableCredits?: number | null;
  planId?: string | null;
  fetchCreditBalance?: (userId: string) => Promise<void> | void;
  onStarted?: (run: any, suggestions: any[]) => void;
  onTopUpCredits?: (context: CreditTopUpContext) => void;
}

const TermScanLaunchModal: React.FC<TermScanLaunchModalProps> = ({
  open,
  onClose,
  bookId,
  pair,
  apiBase,
  resolvedUserId,
  auth,
  availableCredits,
  planId,
  fetchCreditBalance,
  onStarted,
  onTopUpCredits,
}) => {
  const userId = resolvedUserId || auth?.currentUser?.uid || '';
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [estimate, setEstimate] = useState<GlossaryScanEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [forceRescan, setForceRescan] = useState(false);
  const requestIdentity = useRef<LaunchRequestIdentity | null>(null);
  const submitPending = useRef(false);
  useEffect(() => {
    setForceRescan(false);
    requestIdentity.current = null;
  }, [bookId, pair, userId]);

  const { sourceLang, targetLang } = useMemo(() => splitLanguagePair(pair), [pair]);


  const canRun = Boolean(bookId && sourceLang && targetLang);
  const creditCost = glossaryScanCost(estimate, forceRescan);
  const reuseAvailable = estimate?.reuseStatus === 'completed' || estimate?.reuseStatus === 'active';
  const requiresConfirmation = Boolean(estimate?.requiresRescanConfirmation);
  const canChooseFresh = estimate?.reuseStatus !== 'active' && (reuseAvailable || requiresConfirmation);
  const insufficientCredits =
    !requestIdentity.current && typeof availableCredits === 'number' && typeof creditCost === 'number' && availableCredits < creditCost;
  const shortfallCredits = useMemo(() => {
    if (typeof availableCredits !== 'number' || typeof creditCost !== 'number') return null;
    return Math.max(Math.ceil(creditCost - availableCredits), 0);
  }, [availableCredits, creditCost]);
  const handleTopUpCredits = () => {
    if (!onTopUpCredits || typeof availableCredits !== 'number' || typeof creditCost !== 'number') return;
    const requiredCredits = Math.max(0, Math.ceil(creditCost));
    const normalizedAvailable = Math.max(0, Math.floor(availableCredits));
    onTopUpCredits({
      source: 'term_scan_modal',
      requiredCredits,
      availableCredits: normalizedAvailable,
      shortfallCredits: Math.max(requiredCredits - normalizedAvailable, 0),
    });
  };
  const canStart = canRun && Boolean(userId) && !loading && !starting && !insufficientCredits && !error && !estimate?.activeConflict && creditCost !== null && (!requiresConfirmation || forceRescan);
  const overlayLabel = starting ? 'Starting AI glossary scan...' : 'Loading scan estimate...';
  const footerStatus = insufficientCredits
    ? 'Top-up needed'
    : canStart
    ? 'Ready'
    : 'Requirements needed';

  useEffect(() => {
    if (!open) {
      setEstimate(null);
      setError(null);
      setStartError(null);
      return;
    }
    if (fetchCreditBalance) {
      if (userId && availableCredits == null) {
        try {
          void fetchCreditBalance(userId);
        } catch {
          // ignore credit refresh failures here
        }
      }
    }
    if (!canRun) {
      setEstimate(null);
      setError('Select a target language before running an AI glossary scan.');
      setStartError(null);
      return;
    }
    if (!userId) {
      setEstimate(null);
      setError('Sign in to run an AI glossary scan.');
      setStartError(null);
      return;
    }
    let cancelled = false;
    const fetchEstimate = async () => {
      setLoading(true);
      setEstimate(null);
      setError(null);
      try {
        const resp = await axios.get(`${apiBase}/books/${bookId}/term-scan/estimate`, {
          params: {
            userId,
            sourceLang,
            targetLang,
          },
        });
        if (!cancelled) setEstimate(resp?.data || null);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to load term scan estimate', err);
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Unable to load estimate.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchEstimate();
    return () => { cancelled = true; };
  }, [apiBase, availableCredits, bookId, canRun, fetchCreditBalance, open, pair, sourceLang, targetLang, userId]);

  const handleStart = async () => {
    if (!canStart || submitPending.current) return;
    setStartError(null);
    if (!userId) {
      setStartError('Sign in to run an AI glossary scan.');
      return;
    }
    submitPending.current = true;
    setStarting(true);
    try {
      const cachedContext = requestIdentity.current ? JSON.parse(requestIdentity.current.signature) : null;
      const payload = cachedContext
        ? { userId: cachedContext.userId, sourceLang: cachedContext.sourceLang, targetLang: cachedContext.targetLang,
            forceRescan: cachedContext.forceRescan, expectedFingerprint: cachedContext.expectedFingerprint }
        : { userId, sourceLang, targetLang, forceRescan, expectedFingerprint: estimate?.fingerprint };
      requestIdentity.current = launchRequestIdentity(requestIdentity.current, { bookId, ...payload });
      const resp = await axios.post(`${apiBase}/books/${bookId}/term-scan`, { ...payload, requestId: requestIdentity.current.requestId });
      requestIdentity.current = null;
      onStarted?.(resp?.data?.run, resp?.data?.suggestions || []);
      onClose();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to start term scan', err);
      setStartError(err?.response?.data?.detail || err?.message || 'Unable to start glossary scan.');
    } finally {
      submitPending.current = false;
      setStarting(false);
    }
  };

  const isBusy = loading || starting;

  return (
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Run AI glossary scan</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      closable={!starting}
      maskClosable={!starting}
      keyboard={!starting}
      wrapClassName="glossary-scan-modal"
      className="translation-modal-footer-stable term-scan-modal"
      centered
      modalRender={(node) => (
        <div data-tour-ready={isBusy ? 'false' : 'true'}>
          {node}
        </div>
      )}
      footer={[
        <React.Fragment key="summary">
            <div className="translation-modal-footer-summary term-scan-modal-footer-summary" aria-live="polite" data-testid="tutorial:term-scan:launch-footer">
            <span>
              <strong>{formatCredits(creditCost)} credits</strong> required
            </span>
            <span className="translation-modal-footer-summary__separator">·</span>
            <span>
              <strong>{formatCredits(availableCredits)}</strong> available
            </span>
            <span className="translation-modal-footer-summary__separator">·</span>
            <span>
              <strong>{estimate?.chapterCount ?? 0}</strong> chapters
            </span>
            <span className="translation-modal-footer-summary__separator">·</span>
            {insufficientCredits ? (
              <span className="translation-modal-footer-summary__warning">{footerStatus}</span>
            ) : canStart ? (
              <span>{footerStatus}</span>
            ) : (
              <span className="translation-modal-footer-summary__warning">{footerStatus}</span>
            )}
          </div>
        </React.Fragment>,
        <Button key="cancel" onClick={onClose} disabled={starting} data-testid="tutorial:term-scan:launch-cancel">
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          className="app-primary-cta"
          icon={<ThunderboltOutlined />}
          onClick={handleStart}
          loading={starting}
          disabled={!canStart}
          data-testid="tutorial:term-scan:launch-start"
        >
          {reuseAvailable && !forceRescan ? 'Use existing scan' : forceRescan ? 'Start new paid scan' : 'Start AI glossary scan'}
        </Button>,
      ]}
      destroyOnClose
      width="min(720px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
    >
      <div className="translation-modal-body term-scan-modal-body" data-testid="tutorial:term-scan:launch-modal">
        {isBusy && (
          <div
            className="term-scan-modal-overlay"
          >
            <LoadingCard label={overlayLabel} className="term-scan-modal-overlay-card" />
          </div>
        )}
        <section className="translation-modal-block translation-modal-block--scope" data-testid="tutorial:term-scan:launch-scope">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Scan setup</span>
              <h3>Glossary candidate scan</h3>
              <p>Scan all chapters for candidate glossary terms that can be reviewed before applying.</p>
            </div>
          </div>
          <div className="term-scan-modal-scope-grid">
            <div className="term-scan-modal-field">
              <span className="translation-modal-kicker">Source language</span>
              <strong>{sourceLang || 'Unknown'}</strong>
            </div>
            <div className="translation-scope-route-divider term-scan-modal-scope-divider" aria-hidden="true">
              to
            </div>
            <div className="term-scan-modal-field">
              <span className="translation-modal-kicker">Target language</span>
              <strong>{targetLang || 'Unknown'}</strong>
            </div>
          </div>
          <div className="term-scan-modal-helper">
            Suggestions can be reviewed before they become always/never translate glossary rules.
          </div>
        </section>
          <section className="translation-modal-block" data-testid="tutorial:term-scan:launch-estimate">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Readiness</span>
              <h3>Scan summary</h3>
              <p>Confirm language pair, chapter count, and credit estimate before starting.</p>
            </div>
            <span className={`term-scan-readiness-pill ${canStart ? 'is-ready' : 'is-needed'}`}>
              {canStart ? 'Ready' : 'Needed'}
            </span>
          </div>
          <div className="translation-readiness-panel term-scan-modal-summary-panel">
            <Text>
              <strong>Language pair:</strong> {sourceLang || 'Unknown'} to {targetLang || 'Unknown'}
            </Text>
            <Text>
              <strong>Chapters to scan:</strong> {estimate?.chapterCount ?? 'Loading...'}
            </Text>
            <Text type="secondary" className="term-scan-modal-helper">
              Credits are based on total word count across all chapters (0.1 credits per word, rounded).
            </Text>
          </div>
        </section>
        {reuseAvailable && !forceRescan && (
          <Alert type="info" message={estimate?.reuseStatus === 'active'
            ? 'An existing scan is active. Continue with it for 0 additional credits.'
            : 'The completed scan matches this manuscript and language. Reuse its suggestions for 0 additional credits.'} />
        )}
        {estimate?.activeConflict && (
          <Alert type="warning" message="Wait for the existing glossary scan to finish before starting another scan." />
        )}
        {requiresConfirmation && (
          <Alert type="warning" message="The previous scan does not verify the current manuscript. Confirm a new paid scan before starting." />
        )}
        {canChooseFresh && (
          <Checkbox checked={forceRescan} disabled={isBusy} onChange={event => {
            setForceRescan(event.target.checked);
            requestIdentity.current = null;
          }}>
            I confirm a new scan of all chapters for {formatCredits(estimate?.freshCreditCost)} credits.
          </Checkbox>
        )}
        {(error || startError) && (
          <Alert
            className="modal-alert-compact"
            type="error"
            showIcon={false}
            message={error || startError}
          />
        )}
        {!error && insufficientCredits && (
          <InsufficientCreditsAlert
            message="You do not have enough credits to run this scan."
            shortfallCredits={shortfallCredits}
            description="Purchase more credits or reduce scope."
            onTopUpCredits={onTopUpCredits ? handleTopUpCredits : undefined}
          />
        )}
      </div>
    </Modal>
  );
};

export default TermScanLaunchModal;
